import {
    AlertModal,
    Box,
    Button,
    DeleteModal,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownTrigger,
    IconButton,
    Kebab,
    showToast,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    useSubPage,
} from "@bcc-ui/ui-components";
import React from "react";
import { Trans, useTranslation } from "react-i18next";

import { ManageAssetView } from "components/asset-management-system/manage-assets/ManageAssetView";
import { Result } from "components/reports/ManageEditReportView";
import { AUTH_ASSET_DELETE, AUTH_ASSET_EDIT } from "domain/authority";
import { Asset, assetService } from "services/asset-management/AssetService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import { resolveColor } from "utils/commonFunctions";

import testIds from "testIds.json";

const DELETE_CONFIRMATION_WORD = "DELETE";

interface Props {
    asset: Asset;
    isDeleteAllowed: boolean;
    deleteDisableTooltip: string | undefined;
    onAssetEdit?: (asset: Asset) => void;
    onAssetDelete?: (asset: Asset) => void;
    tableRef: React.RefObject<HTMLElement>;
}

const AssetKebabMenu = (props: Props) => {
    const { t } = useTranslation();
    const abortControllerRef = React.useRef<AbortController | undefined>(undefined);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [deleteAssetModalVisible, setDeleteAssetModalVisible] = React.useState(false);
    const [assetNotDeleted, setAssetNotDeleted] = React.useState<boolean>(false);
    const [result, setResult] = React.useState<Result>({ title: "", message: "" });
    const { setSubPage } = useSubPage();

    const handleEdit = () => {
        setSubPage(<ManageAssetView asset={props.asset} onEditDone={(updated) => props.onAssetEdit?.(updated)} />);
    };

    const handleDelete = () => {
        setIsDeleting(true);
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        assetService
            .deleteAsset(props.asset.uuid, abortController)
            .then(() => {
                setIsDeleting(false);
                setDeleteAssetModalVisible(false);
                showToast(t("AssetManagement.deleteAsset.successMessage", { assetId: props.asset.assetId }), "success");
                props.onAssetDelete?.(props.asset);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setIsDeleting(false);
                    setDeleteAssetModalVisible(false);
                    setAssetNotDeleted(true);
                    setResult({
                        title: t("AssetManagement.deleteAsset.assetNotDeletedTitle"),
                        message: t("AssetManagement.deleteAsset.failureMessage"),
                    });
                }
            });
    };

    React.useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const deleteMenuItem = (
        <DropdownMenuItem
            errorStyle
            disabled={!props.isDeleteAllowed || isDeleting}
            onClick={
                props.isDeleteAllowed
                    ? (e) => {
                          usageStatisticsService.sendEvent({
                              category: Category.ASSET_MANAGEMENT,
                              action: Action.REMOVE_ASSET,
                          });
                          setDeleteAssetModalVisible(true);
                          e.preventDefault();
                      }
                    : undefined
            }
            data-testid={testIds.workArea.asset.table.kebabMenu.deleteButton}
        >
            {t("Common.delete")}
        </DropdownMenuItem>
    );

    return (
        <>
            <DropdownMenu
                data-testid={testIds.workArea.asset.table.kebabMenu.itself}
                position={"bottom-start"}
                dropdownOffset={"Xxs"}
                closeOnSelection
                flipOptions={{
                    fallbackPlacements: ["top-start"],
                }}
            >
                <DropdownTrigger>
                    <IconButton>
                        <Kebab color={resolveColor("--theme-icon-fill-default")} />
                    </IconButton>
                </DropdownTrigger>
                <DropdownContent containerRef={props.tableRef}>
                    {userSessionService.userHasAllAuthorities([AUTH_ASSET_EDIT]) && (
                        <DropdownMenuItem
                            onClick={handleEdit}
                            testId={testIds.workArea.asset.table.kebabMenu.editButton}
                        >
                            {t("Common.edit")}
                        </DropdownMenuItem>
                    )}
                    {userSessionService.userHasAllAuthorities([AUTH_ASSET_DELETE]) && (
                        <>
                            <DropdownMenuSeparator />
                            {props.isDeleteAllowed && props.deleteDisableTooltip === undefined ? (
                                deleteMenuItem
                            ) : (
                                <Tooltip>
                                    <TooltipContent>{props.deleteDisableTooltip}</TooltipContent>
                                    <TooltipTrigger>{deleteMenuItem}</TooltipTrigger>
                                </Tooltip>
                            )}
                        </>
                    )}
                </DropdownContent>
            </DropdownMenu>

            <AlertModal
                title={result.title}
                variant={"Error"}
                buttons={
                    <Box gap={"Xs"}>
                        <Button
                            variant={"Primary"}
                            testId={testIds.common.dialog.retryButton}
                            onClick={() => {
                                setAssetNotDeleted(false);
                                handleDelete();
                            }}
                        >
                            {t("Common.deleteAgain")}
                        </Button>
                        <Button
                            variant={"Secondary"}
                            testId={testIds.common.dialog.closeButton}
                            onClick={() => setAssetNotDeleted(false)}
                        >
                            {t("Common.close")}
                        </Button>
                    </Box>
                }
                isOpen={assetNotDeleted}
                hideModal={() => setAssetNotDeleted(false)}
            >
                <Box>{t("Common.failureMessage")}</Box>
            </AlertModal>

            <DeleteModal
                title={t("AssetManagement.deleteAsset.title")}
                isOpen={deleteAssetModalVisible}
                hideModal={() => setDeleteAssetModalVisible(false)}
                onDelete={handleDelete}
                textBundle={{
                    confirmationText: DELETE_CONFIRMATION_WORD,
                    deleteButtonText: t("AssetManagement.deleteAsset.delete"),
                    cancelButtonText: t("Common.cancel"),
                }}
                dataType={"DELETE_ASSET"}
            >
                <Trans
                    i18nKey="AssetManagement.deleteAsset.introductionMessage"
                    values={{ assetId: props.asset.assetId }}
                    components={{ p: <p /> }}
                />
            </DeleteModal>
        </>
    );
};

export default AssetKebabMenu;
